import { computed, inject, ref, watch } from 'vue'
import { defineStore } from 'pinia'

import { WSInstance, WSResponse } from '@/services/ws/utils/types'
import type { AIChatMessageData } from '../utils/types'
import { AssetWizardResponse } from './utils/types'
import { AIType } from '../utils/enums'

import { TRANSACTION_TYPE } from '@/entities/transactions/utils/const'
import { generateId } from '@/entities/utils/helpers'
import { getCurrentDate } from '@/helpers/dates'
import { handleCatchedError } from '@/helpers/common'
import { prepareMessages } from '../utils/helpers'
import { useAIPortfolioContext } from '../utils/context'
import { PRIVATE_SECURITY_TYPE_EXCLUDES } from '../utils/const'
import { createSuggestionTimeout } from '../utils/timeout'

import { useAssetsStore } from '@/store/assets'
import { useRepositoriesStore } from '@/store/repositories'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'
import { useUserStore } from '@/store/user'

/**
 * The assistant behind "Type what happened" in the Add asset dialog.
 *
 * One sentence in, one structured answer out: which wizard flow this is, and
 * whatever fields the sentence actually supported. It deliberately does not
 * behave like the agent conversation — nothing is persisted in a conversation
 * list, and it never interrogates the user, because the wizard that opens
 * afterwards is the thing that collects what is missing.
 */
export const useAIAssetWizardStore = defineStore('ai-asset-wizard', () => {
  // INIT
  const assetsStore = useAssetsStore()
  const repositoriesStore = useRepositoriesStore()
  const transactionsSettingsStore = useTransactionsSettingsStore()
  const userStore = useUserStore()

  const {
    accountsList,
    currenciesList,
    assetTypesFor,
    allAccountTypes,
  } = useAIPortfolioContext()

  const ws = inject<WSInstance>('ws')

  /*
   * The request id the websocket echoes back. Held in memory only: a read of one
   * sentence is worth nothing once the dialog closes, so there is no thread to
   * restore and nothing to persist.
   */
  const requestId = ref(`tmp_${generateId()}`)

  /*
   * A websocket that goes quiet leaves `loading` true forever and
   * WizardReading with no error or retry to show — the same quiet-socket
   * failure `createSuggestionTimeout` was added to prevent for the
   * suggestion assistants.
   */
  const timeout = createSuggestionTimeout()

  const sentence = ref('')
  const answer = ref<AssetWizardResponse>()
  const loading = ref(false)
  const errorMessage = ref<string>()

  // GETTERS

  const getAnswer = computed(() => answer.value)

  const getSentence = computed(() => sentence.value)

  const getErrorMessage = computed(() => errorMessage.value)

  const isLoading = computed(() => loading.value)

  // SETTERS

  const setErrorMessage = (data?: string) => {
    errorMessage.value = data
  }

  // ACTIONS

  /**
   * Sends the sentence and starts a fresh read. Every call is a new thread: the
   * dialog shows one answer at a time, and a stale thread would have the model
   * answering about the previous sentence.
   */
  const submit = async (content: string) => {
    reset()

    sentence.value = content

    // The websocket is absent when VITE_APP_WS_DISABLED is set. Said out loud
    // rather than left spinning on a request that will never be sent.
    if (!ws) {
      setErrorMessage('The assistant is unavailable — realtime is switched off in this build')
      return
    }

    loading.value = true

    /*
     * Two things the prompt is rendered from that nothing on the Add asset path
     * has necessarily loaded — this dialog opens from the app shell, not from a
     * transactions or a data view.
     *
     * Awaited rather than fired off. Sending first renders the prompt with an
     * empty list of allowed account types and an empty list of accounts, and
     * the model then answers against a repository that looks like it owns
     * nothing: no account can be matched, so every record it describes has its
     * account created by hand. Loading them here also puts them in place before
     * the answer arrives, which is when the wizard checks the id it came back
     * with.
     */
    try {
      await Promise.all([
        transactionsSettingsStore.initFlag ? undefined : transactionsSettingsStore.fetch(),
        assetsStore.initFlag ? undefined : assetsStore.fetch(),
      ])
    } catch (e) {
      loading.value = false
      setErrorMessage('Could not load this repository\'s accounts')
      handleCatchedError(e as string)
      return
    }

    const { messages } = prepareMessages(content)

    const messageData: AIChatMessageData = {
      ai_type: AIType.ASSET_WIZARD,
      messages,
      dynamic_context: {
        today: getCurrentDate({ iso: true }),
        accounts_list: accountsList.value,
        currencies_list: JSON.stringify(currenciesList.value),
        default_currency: repositoriesStore.getCurrentCurrency,
        /*
         * The union across every transaction a wizard flow actually posts
         * (add position, add liability, subscribe and commit, subscribe and
         * fund — see `allAccountTypes`), not one list per transaction: the
         * classifier no longer needs to know which flow a type belongs to,
         * only whether it is one the repository has at all for something a
         * wizard creates. Merging trades away the (currently theoretical)
         * case where a repository configures two of those transactions'
         * account types differently: if that ever happens here, a type
         * valid for one but not another could be offered for both.
         */
        account_types: JSON.stringify(allAccountTypes.value),
        asset_types: JSON.stringify(
          assetTypesFor(TRANSACTION_TYPE.SUBSCRIBE_AND_FUND)
            .filter(type => !PRIVATE_SECURITY_TYPE_EXCLUDES.includes(type)),
        ),
      },
    }

    timeout.start(() => {
      loading.value = false
      setErrorMessage('The assistant timed out — no answer for the sentence')
      handleCatchedError('Asset wizard read timed out — no answer for the sentence')
    })

    ws.send('ai', 'chat', requestId.value, messageData)
  }

  const retry = () => {
    if (!sentence.value) return
    submit(sentence.value)
  }

  const reset = () => {
    requestId.value = `tmp_${generateId()}`
    sentence.value = ''
    answer.value = undefined
    timeout.stop()
    loading.value = false
    setErrorMessage(undefined)
  }

  watch(
    () => userStore.isAuthenticated,
    value => {
      if (!value) return
      ws?.subscribe(
        'ai',
        'chat',
        (message: WSResponse<string>, responseId: string) => {
          // The agent and dashboard assistants share this channel; the request
          // id is what says the answer is ours.
          if (responseId !== requestId.value) return

          try {
            if (message.errors) {
              setErrorMessage(message.errors.join(', '))
              return
            }
            if (!message.data) {
              setErrorMessage('The assistant returned an empty answer')
              return
            }
            answer.value = JSON.parse(message.data) as AssetWizardResponse
          } catch (e) {
            setErrorMessage('The assistant returned an answer we could not read')
            handleCatchedError(e as string)
          } finally {
            timeout.stop()
            loading.value = false
          }
        },
      )
    },
    { immediate: true },
  )

  return {
    isLoading,

    getAnswer,
    getSentence,
    getErrorMessage,

    submit,
    retry,
    reset,
  }
})
